import moment from "moment";
import React from "react";
import Chart from "react-apexcharts";
import { usePriceFormatter } from "../../../hooks";

type CandlestickChartProps = {
  data: { x: string; y: [number, number, number, number] }[]; // Data in OHLC format [Open, High, Low, Close]
  width?: string | number;
  height?: string | number;
  timeline?: string; // Add timeline prop
};

const CandlestickChart: React.FC<CandlestickChartProps> = ({
  data,
  width = "100%",
  height = "300px",
  timeline, // Destructure timeline prop
}) => {
  const formatter = usePriceFormatter();
  const options: ApexCharts.ApexOptions = {
    chart: {
      id: "candlestick-chart",
      type: "candlestick",
      background: "transparent",
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      type: "category",
      labels: {
        style: { colors: "white" },
        rotate: 0,
        formatter: (value) => {
          const date = moment(value);
          if (timeline === "1 Day") {
            return date.format("HH:mm");
          } else if (timeline === "5 Day" || timeline === "1 Month") {
            return date.format("MMM/DD");
          } else if (timeline === "6 Month" || timeline === "1 Year" || timeline === "5 Years" || timeline === "All") {
            return date.format("MMM/YY");
          }
          return date.format("DD MMM HH:mm"); // Default fallback
        },
      },
      axisTicks: { show: false },
      crosshairs: {
        show: true,
        stroke: {
          color: '#999',
          width: 1,
          dashArray: 3,
        },
      },
    },
    yaxis: {
      tooltip: {
        enabled: true, // 🔥 This enables the horizontal line on hover
      },
      labels: {
        show: true,
        style: { colors: "white" },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
        const ohlc = dataPoint?.y ?? [];
        const date = moment(dataPoint.x).format(timeline === "1 Day" ? "MM-DD-YYYY HH:mm" : "MM-DD-YYYY");
        const open = ohlc[0] ?? 'N/A';
        const high = ohlc[1] ?? 'N/A';
        const low = ohlc[2] ?? 'N/A';
        const close = ohlc[3] ?? 'N/A';

        return `<div style="background-color: #333; color: #fff; padding: 10px; border-radius: 5px;">
                <span>Date: ${date}</span><br/>
                <span>Open: ${formatter.display(open, { showSymbol: true, })}</span><br/>
                <span>High: ${formatter.display(high, { showSymbol: true, })}</span><br/>
                <span>Low: ${formatter.display(low, { showSymbol: true, })}</span><br/>
                <span>Close: ${formatter.display(close, { showSymbol: true, })}</span><br/>
                <span>Volume: ${formatter.display(dataPoint?.v, { compact: true, showSymbol: false })}</span>
              </div>`;
      },
    },
    plotOptions: {
      candlestick: {
        colors: {
          downward: '#FF0000',
          upward: '#00FF00',
        },
      },
    },
    grid: { show: false },
  };


  const series: ApexAxisChartSeries = [
    {
      name: "Candle",
      data: data,
    },
  ];

  return (
    <div className="candlestick-chart">
      <Chart options={options} series={series} type="candlestick" width={width} height={height} />
    </div>
  );
};

export default CandlestickChart; 